import React, { useContext } from "react";

import { companyUrl, productUrl, toAbsoluteUrl } from "views_shared/app/javascript/utils/urls";
import EntityHeader from "../../../profiles/EntityHeader";
import ProfileImage from "../../../ProfileImage";
import FollowControl from "../../../profiles/FollowControl";
import ShareProfileButton from "../../../profiles/ShareProfileButton";
import MessageButton from "../../../MessageButton";
import { ProductContext } from "../../data/product_context_management";
import AddSectionButton from "./add_sections_button";
import VisibilityBadge from "../../reusable/visibility_badge";

const productProfileBannerImage = RAILS_ASSET_URL("/product-profile-banner.png");

// Product's engine header on the shared <EntityHeader> shell. Products carry no cover upload
// feature (unlike Company), so the `banner` slot always renders the one generated default asset —
// the same cover for every product, same as Media Outlet's shared default. The company eyebrow
// (a plain underlined link, no logo — the frame's own treatment; the rail's separate "Company
// Profile" card keeps the logo+link version), name and price stack in the identity column; the
// visibility badge rides the aside. Messaging moved to the rail's Connect card (you never message
// an entity you represent, and the rail is the one talk-to-these-people place). The actions row is
// Follow (viewers only — you don't follow your own product) + View Website + Share Profile.
function ProductHeader() {
  const { product, viewerContext, setOpenEditModal, silentReload } = useContext(ProductContext);
  const editable = !!viewerContext?.editable;

  const banner = (
    <div className="person-header-banner">
      <img src={productProfileBannerImage} alt="" />
    </div>
  );

  // The framed entity-avatar variant the Company and Media Outlet headers use: the image is
  // contained in its tile rather than cropped to fill it. Editing is in-place over the image
  // itself (hover for the replace/remove controls), the same interaction as the sibling logos.
  const avatar = (
    <ProfileImage
      editable={editable}
      imgOwnerId={product.id}
      imgOwnerName={product.name}
      imgOwnerType="product"
      profileImageUrl={product.profile_image_url}
      onChange={silentReload}
      framed
    />
  );

  const identity = (
    <>
      {product.company_name && product.company_slug && (
        <a
          href={companyUrl({ slug: product.company_slug, migrate_id: product.company_migrate_id })}
          className="text-decoration-underline text-body-secondary"
        >
          {product.company_name}
        </a>
      )}

      {/* Owner controls live on the name line, not in the actions row: the pen is the same header
          edit affordance every person engine uses, and Add-a-section sits beside it so the pinned
          row stays purely audience-facing — an owner button styled like the public pair reads as
          broken rather than privileged. */}
      <div className="d-flex flex-wrap align-items-center mt-2">
        <h1 className="fw-bold p-0 m-0">{product.name}</h1>
        {editable && (
          <>
            <button
              type="button"
              aria-label="Edit Header"
              className="btn btn-primary btn-sm ms-2 d-inline-flex align-items-center justify-content-center"
              style={{ width: "2rem", height: "2rem", padding: 0 }}
              onClick={() => setOpenEditModal("HEADER")}
            >
              <i className="fa-solid fa-pencil-alt" />
            </button>
            <AddSectionButton />
          </>
        )}
      </div>

      {product.headline && <h2 className="h5 text-body-secondary mt-1 mb-0">{product.headline}</h2>}

      {/* A price of 0 is a real price, so this asks for a value, not for truthiness. */}
      {product.price != null && (
        <h3 className="mt-1 fw-normal" aria-label="price">
          ${product.price}
        </h3>
      )}
    </>
  );

  const canonicalProductUrl = toAbsoluteUrl(productUrl({ slug: product.slug, migrate_id: product.migrate_id }));

  const actions = (
    <>
      {!viewerContext?.isSelf && <FollowControl followableType="Id::PromoEntity::Product" followableId={product.id} />}
      {product.product_url && (
        <a
          href={product.product_url}
          className="btn btn-outline-primary text-nowrap d-block mb-2 me-2"
          target="_blank"
          rel="noopener noreferrer"
        >
          View Website
        </a>
      )}
      {!viewerContext?.isSelf && !!viewerContext?.isAuthenticated && (
        <MessageButton
          promoEntityId={product.id}
          promoEntityType="Id::PromoEntity::Product"
          iconClassName="fa-regular fa-message"
        />
      )}
      <ShareProfileButton url={canonicalProductUrl} />
    </>
  );

  // The badge reports the product's current visibility. It edits nothing, so it follows the
  // owner, not the write switch.
  const aside = viewerContext?.isSelf ? <VisibilityBadge value={product.visibility} /> : null;

  return (
    <EntityHeader
      banner={banner}
      avatar={avatar}
      avatarWrapperClassName="product-avatar-frame"
      identity={identity}
      actions={actions}
      aside={aside}
    />
  );
}

export default ProductHeader;
